/**
 * @file Region Tooltip Manager
 * @description Tooltip management for region component
 */

import { SELECTORS } from "./constants.js";
import { getTermIDFromSlug, generateTooltipContent } from "./utils.js";
import { fetchRegionData, fetchRegionDataBySlug } from "./api-service.js";
import { setTippyManager, getTippyManager } from "./state.js";

/**
 * Initialize Tippy Manager for region tooltips
 */
export async function initializeTippyManager() {
  try {
    const { getTippyManager: getGlobalTippyManager } = await import(
      "../../services/tippy-manager.js"
    );
    const tippyManager = getGlobalTippyManager();
    setTippyManager(tippyManager);
    tippyManager.initSelector(SELECTORS.MAPSVG_REGIONS, "region");
  } catch (error) {
    console.error("Failed to load Tippy Manager for region tooltips:", error);
  }
}

/**
 * Handle region mouse over event for tooltips
 * @param {Event} e - Mouse event
 */
export async function handleRegionMouseOver(e) {
  const regionID = e.currentTarget.id;

  try {
    // Check if this is a vietnam-map region or child-map region
    const isVietnamMapRegion = e.currentTarget.closest(SELECTORS.VIETNAM_MAP_REGIONS);
    const isChildMapRegion = e.currentTarget.closest(SELECTORS.CHILD_MAP_REGIONS);

    let data;

    if (isVietnamMapRegion) {
      // For vietnam-map regions, use slug to fetch data (like in original code)
      data = await fetchRegionDataBySlug(regionID);
      if (!data) {
        console.error("No data found for vietnam-map region:", regionID);
        return;
      }
    } else if (isChildMapRegion) {
      // For child-map regions, use the region ID directly as slug
      data = await fetchRegionDataBySlug(regionID);
      if (!data) {
        console.error("No data found for child-map region:", regionID);
        return;
      }
    } else {
      // Skip if not a valid region
      return;
    }

    // Get Tippy Manager instance
    const tippyManager = getTippyManager();
    if (!tippyManager) {
      console.error("Tippy Manager not initialized");
      return;
    }

    // Check if element already has a Tippy instance
    let instance = e.currentTarget._tippy;

    if (!instance) {
      const config = tippyManager.configs.get("region");
      if (config) {
        instance = tippyManager.createInstance(e.currentTarget, config, "region");
      }
    }

    if (!instance) {
      console.error("Failed to create Tippy instance for region:", regionID);
      return;
    }

    // Set loading content
    instance.setContent("Loading...");

    // Generate and set tooltip content
    const contentHTML = generateTooltipContent(data);
    instance.setContent(contentHTML);
  } catch (error) {
    console.error("Error in regionMouseOver:", error);
  }
}

/**
 * Handle region mouse leave event for tooltips
 * @param {Event} e - Mouse event
 */
export function handleRegionMouseLeave(e) {
  const regionID = e.currentTarget.id;

  try {
    // Hide tooltip if it exists
    if (e.currentTarget._tippy) {
      e.currentTarget._tippy.hide();
    }
  } catch (error) {
    console.error("Error in regionMouseLeave:", error);
  }
}

/**
 * Create tooltip for element
 * @param {HTMLElement} element - Target element
 * @param {string} content - Tooltip content
 * @param {Object} options - Tooltip options
 * @returns {Object|null} Tippy instance
 */
export async function createTooltip(element, content, options = {}) {
  try {
    const tippyManager = getTippyManager();
    if (!tippyManager) {
      console.error("Tippy Manager not initialized");
      return null;
    }

    const config = {
      content: content,
      ...options,
    };

    return tippyManager.createInstance(element, config, "custom");
  } catch (error) {
    console.error("Error creating tooltip:", error);
    return null;
  }
}

/**
 * Update tooltip content
 * @param {HTMLElement} element - Target element
 * @param {string} content - New tooltip content
 */
export function updateTooltipContent(element, content) {
  if (element._tippy) {
    element._tippy.setContent(content);
  }
}

/**
 * Show tooltip
 * @param {HTMLElement} element - Target element
 */
export function showTooltip(element) {
  if (element._tippy) {
    element._tippy.show();
  }
}

/**
 * Hide tooltip
 * @param {HTMLElement} element - Target element
 */
export function hideTooltip(element) {
  if (element._tippy) {
    element._tippy.hide();
  }
}

/**
 * Destroy tooltip
 * @param {HTMLElement} element - Target element
 */
export function destroyTooltip(element) {
  if (element._tippy) {
    element._tippy.destroy();
  }
}

/**
 * Get tooltip instance
 * @param {HTMLElement} element - Target element
 * @returns {Object|null} Tippy instance
 */
export function getTooltipInstance(element) {
  return element._tippy || null;
}

/**
 * Check if element has tooltip
 * @param {HTMLElement} element - Target element
 * @returns {boolean} Whether element has tooltip
 */
export function hasTooltip(element) {
  return !!element._tippy;
}

/**
 * Initialize tooltips for selector
 * @param {string} selector - CSS selector
 * @param {string} configKey - Configuration key
 */
export async function initTooltipsForSelector(selector, configKey = "default") {
  try {
    const tippyManager = getTippyManager();
    if (!tippyManager) {
      console.error("Tippy Manager not initialized");
      return;
    }

    tippyManager.initSelector(selector, configKey);
  } catch (error) {
    console.error("Error initializing tooltips for selector:", error);
  }
}

/**
 * Get tooltip statistics
 * @returns {Object} Tooltip statistics
 */
export function getTooltipStats() {
  const tippyManager = getTippyManager();
  if (!tippyManager) {
    return { initialized: false };
  }

  return tippyManager.getStats();
}
